import React from 'react';
import DataList from './DataList';
import ApiConfigFactory from '../configurations/ApiConfigFactory';

const DoctorCommissionConfig = () => {
    // Initialize apiConfig using ApiConfigFactory
    const apiConfig = new ApiConfigFactory('employees/doctor-commission-configs', 'Doctor Commission Configuration');
    
    // Configure basic settings
    apiConfig.defaultSortAsc = false;
    apiConfig.defaultSortFieldId = 'id';
    apiConfig.useLocalCaching = false;
    apiConfig.allowExport = true;
    apiConfig.path = '/doctor-commission-config';

    // Custom validation for commission rates
    apiConfig.additionalValidation = (formData) => {
        let error = {};
        
        if (!formData.doctor_id) {
            error.message = 'Doctor is required';
        }
        
        if (!formData.commission_rates || !Array.isArray(formData.commission_rates)) {
            error.message = 'Commission rates are required';
        } else if (formData.commission_rates.length === 0) {
            error.message = 'At least one commission rate must be configured';
        } else {
            // Validate each commission rate
            for (let i = 0; i < formData.commission_rates.length; i++) {
                const rate = formData.commission_rates[i];
                
                if (!rate.category || rate.category.trim() === '') {
                    error.message = `Category is required for commission rule ${i + 1}`;
                    break;
                }
                
                if (rate.rate === undefined || rate.rate === null || rate.rate === '') {
                    error.message = `Commission rate is required for rule ${i + 1}`;
                    break;
                }
                
                const rateValue = parseFloat(rate.rate);
                if (isNaN(rateValue) || rateValue < 0 || rateValue > 100) {
                    error.message = `Commission rate for rule ${i + 1} must be between 0 and 100`;
                    break;
                }
            }
            
            // Check for duplicate categories
            if (!error.message) {
                const categories = formData.commission_rates.map(rate => rate.category.toLowerCase().trim());
                const uniqueCategories = [...new Set(categories)];
                if (categories.length !== uniqueCategories.length) {
                    error.message = 'Duplicate commission categories are not allowed';
                }
            }
        }
        
        if (error.message) {
            error.isValid = false;
        } else {
            error.isValid = true;
        }
        return error;
    };

    // Configure form fields
    apiConfig
        .addField({ 
            name: 'id', 
            label: 'ID', 
            type: 'text', 
            identifier: true, 
            visible: false, 
            tab: 'Main' 
        })
        .addField({ 
            name: 'doctor_id', 
            label: 'Doctor', 
            type: 'dropdown', 
            required: true, 
            tab: 'Main' 
        })
        .addField({ 
            name: 'commission_rates', 
            label: 'Commissions', 
            type: 'subform', 
            required: true, 
            tab: 'Main',
            defaultValue: [],
            subFields: [
                {
                    name: 'category',
                    label: 'Category',
                    type: 'dropdown',
                    required: true,
                    options: [
                        { value: 'admission', label: 'Admission' },
                        { value: 'consultation', label: 'Consultation' },
                        { value: 'surgery', label: 'Surgery' },
                        { value: 'ultrasound', label: 'Ultrasound' },
                        { value: 'lab', label: 'Lab' },
                        { value: 'medication', label: 'Medication' },
                        { value: 'radiology', label: 'Radiology' },
                        { value: 'observation', label: 'Observation' },
                        { value: 'echocardiogram', label: 'Echocardiogram' },
                        { value: 'procedure', label: 'Procedure' }
                    ]
                },
                {
                    name: 'rate',
                    label: 'Commission Rate (%)',
                    type: 'number',
                    required: true,
                    min: 0,
                    max: 100,
                    step: 0.01,
                    width: '150px',
                    placeholder: 'e.g., 10.5'
                }
            ]
        })
        .addField({ 
            name: 'created_by', 
            label: 'Created By', 
            type: 'text', 
            disabled: true, 
            tab: 'Audit' 
        })
        .addField({ 
            name: 'updated_by', 
            label: 'Updated By', 
            type: 'text', 
            disabled: true, 
            tab: 'Audit' 
        })

    // Add dropdown configurations
    .addDropdownField({
        name: 'doctor_id',
        endpoint: '/hospital-portal/api/employees/doctors',
        valueField: 'doctor_id',
        labelField: 'doctor_name',
    })

    // Add form tabs
    .addTab({ name: 'Main', roles: 'all', default: true })
    .addTab({ name: 'Audit', roles: 'admin' })

    // Configure data table columns
    .addColumn({ 
        id: 'id', 
        name: 'ID', 
        selector: (row) => row.id, 
        type: 'text', 
        sortable: true, 
        identifier: true, 
        width: '80px', 
        omit: true 
    })
    .addColumn({ 
        name: 'Doctor', 
        selector: (row) => row.doctor_name, 
        sortable: true, 
        filterable: true, 
        filterSelector: 'doctor_name',
        minWidth: '200px'
    })
    .addColumn({ 
        name: 'Speciality', 
        selector: (row) => row.doctor_speciality, 
        sortable: true, 
        filterable: true, 
        filterSelector: 'doctor_speciality',
        minWidth: '150px'
    })
    .addColumn({ 
        name: 'Commission Rules', 
        selector: (row) => row.commission_count || 0, 
        sortable: true, 
        width: '150px',
        cell: (row) => (
            <span className="badge badge-info">
                {row.commission_count || 0} rules
            </span>
        )
    })
    .addColumn({ 
        name: 'Doctor Status', 
        selector: (row) => row.doctor_status, 
        sortable: true, 
        filterable: true, 
        filterSelector: 'doctor_status',
        width: '120px',
        cell: (row) => (
            <span className={`status-badge ${row.doctor_status === 'Active' ? 'active' : 'inactive'}`}>
                {row.doctor_status}
            </span>
        )
    })
    .addColumn({ 
        name: 'Created By', 
        selector: (row) => row.created_by || 'System', 
        sortable: true, 
        width: '120px'
    })
    .addColumn({ 
        name: 'Updated', 
        selector: (row) => {
            if (row.updatedAt) {
                return new Date(row.updatedAt).toLocaleDateString();
            }
            return 'N/A';
        }, 
        sortable: true, 
        type: 'date',
        width: '120px'
    });

    return (
        <div className="doctor-commission-config">
            <div className="page-header">
                <h2>Doctor Commission Configuration</h2>
                <p>Manage commission rates for different service categories per doctor. Select from predefined categories like Consultation, Surgery, Lab, etc., and set the corresponding commission rates.</p>
            </div>
            
            <DataList apiConfig={apiConfig} columns={apiConfig.columns} />
            
            <style jsx>{`
                .doctor-commission-config .page-header {
                    margin-bottom: 20px;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border-left: 4px solid #007bff;
                }
                
                .doctor-commission-config .page-header h2 {
                    margin: 0 0 5px 0;
                    color: #333;
                    font-size: 1.5rem;
                }
                
                .doctor-commission-config .page-header p {
                    margin: 0;
                    color: #666;
                    font-size: 0.9rem;
                }
                
                .status-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    text-transform: uppercase;
                }
                
                .status-badge.active {
                    background-color: #d4edda;
                    color: #155724;
                    border: 1px solid #c3e6cb;
                }
                
                .status-badge.inactive {
                    background-color: #f8d7da;
                    color: #721c24;
                    border: 1px solid #f5c6cb;
                }
                
                .badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .badge-info {
                    background-color: #d1ecf1;
                    color: #0c5460;
                    border: 1px solid #bee5eb;
                }
            `}</style>
        </div>
    );
};

export default DoctorCommissionConfig;